import { useState, type FormEvent } from 'react';
import { useSearchParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { ArrowLeft, FileText } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { SubmitButton } from '@/components/ui/app/submit-button';
import { useSafeBack } from '@/lib/hooks/use-safe-back';
import { useTheme } from '@/lib/contexts/theme-context';
import { horodatyApi } from '@/lib/utils/horodaty-api';

/**
 * Page publique de vérification d'un certificat photo.
 *
 * Mise en page en deux colonnes :
 *   - à gauche : l'illustration de fond (avec le logo du thème superposé)
 *   - à droite : le formulaire sur fond blanc
 *
 * L'utilisateur fournit deux informations liées au certificat :
 *   - un identifiant unique : « Numéro du certificat »
 *   - un code : « Clé d'accès »
 *
 * Après vérification, le PDF du certificat est ouvert dans un nouvel onglet.
 *
 * Le branding (logo, couleurs) est fourni par le système de thème (key_app)
 * via useTheme(). Aucune authentification n'est requise : le gateway proxy
 * injecte automatiquement la clé d'application sur la route publique.
 */
export default function CertificateVerifyPage() {
    const { t } = useTranslation('public');
    const { theme } = useTheme();
    const { goBack } = useSafeBack();
    const [searchParams] = useSearchParams();

    // Le lien du certificat PDF porte le numéro en paramètre (?id=…) : on pré-remplit le champ
    const [number, setNumber] = useState(searchParams.get('id') ?? '');
    const [accessKey, setAccessKey] = useState('');
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [error, setError] = useState<string | null>(null);
    const [success, setSuccess] = useState(false);
    const [showHelp, setShowHelp] = useState(false);

    /**
     * Extrait un message d'erreur lisible d'une réponse en erreur.
     * La réponse étant demandée en blob, un corps d'erreur JSON arrive
     * lui aussi sous forme de blob qu'il faut décoder.
     */
    const resolveErrorMessage = async (err: unknown): Promise<string> => {
        const response = (err as { response?: { status?: number; data?: unknown } })?.response;

        if (!response) {
            return t('certificate.contactFailed');
        }

        // La réponse étant demandée en blob, un corps d'erreur JSON arrive
        // lui aussi sous forme de blob qu'il faut décoder.
        // Format attendu (409) : { message, code, data: { remaining_attempts } }
        if (response.data instanceof Blob) {
            try {
                const text = await response.data.text();
                const json = JSON.parse(text);

                const base =
                    typeof json?.message === 'string' && json.message.trim()
                        ? json.message.trim()
                        : t('certificate.invalidCredentials');

                const remaining = json?.data?.remaining_attempts;
                if (typeof remaining === 'number' && remaining > 0) {
                    return `${base} ${t('certificate.remainingAttemptsSuffix', { count: remaining })}`;
                }

                return base;
            } catch {
                // ignore : on retombe sur le message par défaut selon le statut
            }
        }

        if ([409, 404, 403, 422].includes(response.status ?? 0)) {
            return t('certificate.invalidCredentialsDetailed');
        }

        return t('certificate.verificationFailed');
    };

    const handleSubmit = async (e: FormEvent) => {
        e.preventDefault();
        if (isSubmitting) return;

        setError(null);
        setSuccess(false);
        setIsSubmitting(true);

        try {
            const res = await horodatyApi.verifyCertificate({
                number: number.trim(),
                accessKey: accessKey.trim(),
            });

            const blob: Blob = res.data;
            const pdfBlob = blob.type ? blob : new Blob([blob], { type: 'application/pdf' });

            // Ouvrir le PDF dans un nouvel onglet pour visualisation (inline)
            const url = URL.createObjectURL(pdfBlob);
            window.open(url, '_blank', 'noopener,noreferrer');

            // Libérer l'URL après un délai laissant le temps au nouvel onglet de charger
            setTimeout(() => URL.revokeObjectURL(url), 60_000);

            setSuccess(true);
        } catch (err) {
            setError(await resolveErrorMessage(err));
        } finally {
            setIsSubmitting(false);
        }
    };

    const handleReset = () => {
        setSuccess(false);
        setError(null);
        setNumber('');
        setAccessKey('');
    };

    return (
        // body et #app ne défilent jamais (app.blade.php) : la page porte son propre défilement, comme WelcomePage
        <div className="h-full w-full overflow-y-auto flex flex-col lg:flex-row p-12 bg-white">
            {/* Colonne gauche : illustration de fond + logo du thème */}
            <div
                className="relative hidden lg:block lg:w-1/2 bg-[#1b2333] bg-cover bg-center bg-no-repeat elative w-full h-full bg-foreground-dark rounded-2xl overflow-hidden"
                style={{ backgroundImage: 'url(/assets/images/certificate/background.png)' }}
            >
            </div>

            {/* Colonne droite : formulaire sur fond blanc */}
            <div className="flex flex-col w-full lg:w-1/2 bg-white min-h-full px-6 py-8 sm:px-10 lg:px-16">

                <div className="theme-logo-container h-8">
                    <img
                        src={theme.logo}
                        alt={theme.logoAlt}
                        className="theme-logo theme-logo-desktop h-full w-auto object-contain object-left"
                    />
                </div>

                <div className="flex-1 flex flex-col justify-center max-w-md w-full mx-auto py-10">
                    {success ? (
                        <div>
                            <div className="w-16 h-16 bg-success/10 rounded-full flex items-center justify-center mb-6">
                                <FileText className="h-8 w-8 text-success" />
                            </div>
                            <h1 className="text-3xl font-black leading-tight mb-4">
                                {t('certificate.verifiedTitle')}
                            </h1>
                            <p className="text-[15px] text-muted-foreground mb-8">
                                {t('certificate.verifiedDescription')}
                            </p>
                            <Button
                                variant="soft"
                                color="neutral"
                                onClick={handleReset}
                                className="w-full h-auto rounded-xl py-4 text-[15px]"
                            >
                                {t('certificate.retryButton')}
                            </Button>
                        </div>
                    ) : (
                        <>
                            <h1 className="text-4xl lg:text-[42px] font-black leading-[1.1] mb-4">
                                {t('certificate.titleLine1')}
                                <br />
                                {t('certificate.titleLine2')}
                            </h1>
                            <p className="text-[15px] text-muted-foreground mb-10">
                                {t('certificate.description', { themeName: theme.name })}
                            </p>

                            <form className="space-y-4" onSubmit={handleSubmit}>
                                <Input
                                    type="text"
                                    autoComplete="off"
                                    placeholder={t('certificate.numberPlaceholder')}
                                    value={number}
                                    onChange={(e) => setNumber(e.target.value)}
                                    required
                                />
                                <Input
                                    type="text"
                                    autoComplete="off"
                                    placeholder={t('certificate.accessKeyPlaceholder')}
                                    value={accessKey}
                                    onChange={(e) => setAccessKey(e.target.value)}
                                    required
                                />

                                {error && (
                                    <div className="bg-destructive/10 text-destructive-accessible rounded-xl px-4 py-3 text-sm">
                                        {error}
                                    </div>
                                )}

                                <SubmitButton
                                    isSubmitting={isSubmitting}
                                    loadingText={t('certificate.verifyingEllipsis')}
                                    disabled={isSubmitting || !number.trim() || !accessKey.trim()}
                                    className="w-full h-auto rounded-xl py-4 text-[15px] font-semibold uppercase tracking-wide mt-2"
                                >
                                    {t('certificate.verifyButton')}
                                </SubmitButton>
                            </form>

                            <div className="text-center mt-8">
                                <Button
                                    type="button"
                                    variant="link"
                                    color="success"
                                    onClick={() => setShowHelp((v) => !v)}
                                    className="text-[13px] font-semibold uppercase tracking-wide"
                                >
                                    {t('certificate.whereToFind')}
                                </Button>
                                {showHelp && (
                                    <p className="text-[13px] text-muted-foreground mt-3">
                                        {t('certificate.whereToFindAnswer')}
                                    </p>
                                )}
                            </div>
                        </>
                    )}
                </div>
            </div>
        </div>
    );
}
